/* eslint-disable class-methods-use-this */
import get from 'lodash/get';
import pick from 'lodash/pick';
import isObject from 'lodash/isObject';
import isFunction from 'lodash/isFunction';
import extend from 'lodash/extend';
import RequestUrls from '@Constants/RequestUrlsConstants';
import Auth from '@Services/Auth';
import SimpleTmpl from '@Services/SimpleTmpl';

import ExportResource from '@Services/ExportResource';
import { EXPORT_TEXTS, EXPORT_EVENTS, EXPORT_TAB_EVENTS } from '@Services/ExportService/ExportConstants';
import RXService from '@Services/RxService';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';

export class ExportRequestManagerService {
  currentRequestsByType = {};

  /**
   * @ngdoc method
   * @name sendExportRequest
   * @methodOf ExportRequestManagerService
   * @param {String} exportType
   * @param {Object} [payload] Options selected by the user OR jobId of previous export
   * @param {Object} [tracking] Tracking object for AA
   * @returns {Object} defer
   * @description Send a new Export request or resend the last one
   */
  sendExportRequest = (exportType, payload = null, tracking = null) => {
    let exportSubject;
    this.updateCurrentRequestByType(exportType, payload, tracking);

    // eslint-disable-next-line no-param-reassign
    payload = get(this.currentRequestsByType, `${exportType}.payload`);

    if (exportType === 'ChemQuote') {
      exportSubject = ExportResource.sendChemQuoteExportRequest(payload);
    } else {
      exportSubject = ExportResource.sendExportRequest(payload);
    }

    RXService.next(EXPORT_TAB_EVENTS[exportType].exportStarted, exportSubject);

    if (!RXService.subscriptionExist(EXPORT_EVENTS.error)) {
      RXService.subscribe(EXPORT_EVENTS.error, (err) => this._wsErrorsHandler(exportType, err));
    }

    exportSubject.subscribe({
      next: ({ type, data }) => {
        if (type === 'progress') {
          this._wsDeferHandler(exportType, exportSubject, data);
          // eslint-disable-next-line sonarjs/no-duplicated-branches
        } else if (type === 'success') {
          this._wsDeferHandler(exportType, exportSubject, data);
        }
      },
      error: (err) => {
        if (get(err, 'isConnectionError')) {
          RXService.next(EXPORT_TAB_EVENTS[exportType].connectionError, err);
        }
      },
    });

    return exportSubject;
  };

  /**
   * @ngdoc method
   * @name restoreExport
   * @methodOf ExportRequestManagerService
   * @param {String} exportType
   * @param {String} jobId
   * @param {Function} failCallback
   * @returns {Object} defer
   * @description Restore export by jobId, consume error
   */
  restoreExport = (exportType, jobId, failCallback) => {
    this.updateCurrentRequestByType(exportType);

    this.currentRequestsByType[exportType].wsErrorOneTimeConsumer = failCallback;

    const exportRestoreSubject = ExportResource.sendRestoreRequest(
      pick(get(this.currentRequestsByType, `${exportType}.payload`), 'requestId'),
      jobId
    );

    exportRestoreSubject.subscribe({
      next: ({ data }) => {
        this._wsDeferHandler(exportType, exportRestoreSubject, data);
      },
      error: (err) => {
        if (get(err, 'isConnectionError')) {
          RXService.next(EXPORT_TAB_EVENTS[exportType].connectionError, err);
        }
      },
    });

    return exportRestoreSubject;
  };

  /**
   * @ngdoc method
   * @name getFileDownloadUrl
   * @methodOf ExportRequestManagerService
   * @param {String} exportType
   * @param {Object} data Data object with properties coming from WS.
   * @description Construct a link for Export download.
   */
  getFileDownloadUrl = (exportType, data) => {
    let url;
    // eslint-disable-next-line camelcase,prefer-const
    let { access_token, jobId, documentName } = data;
    // eslint-disable-next-line camelcase
    access_token = access_token || Auth.getAccessToken();

    if (exportType === 'ChemQuote') {
      // eslint-disable-next-line camelcase
      url = SimpleTmpl.format(RequestUrls.ChemQuoteDownload.url, { access_token, jobId, documentName });
    } else {
      // eslint-disable-next-line camelcase
      url = SimpleTmpl.format(RequestUrls.printDownload.url, { access_token, jobId, documentName });
    }

    return url;
  };

  /**
   * @ngdoc method
   * @name cleanupCurrentRequestByType
   * @methodOf ExportRequestManagerService
   * @param {String} exportType
   * @description Resets currentRequestByType for specific exportType
   * */
  cleanupCurrentRequestByType = (exportType) => {
    if (this.currentRequestsByType[exportType]) {
      this.currentRequestsByType[exportType] = null;
    }
  };

  /**
   * @ngdoc method
   * @name isBusy
   * @methodOf ExportRequestManagerService
   * @param {String} exportType
   * @description Check if export of exportType is currently running
   */
  isBusy = (exportType) => !!this.currentRequestsByType[exportType];

  /**
   * @ngdoc method
   * @name getTrackingByExportType
   * @methodOf ExportRequestManagerService
   * @param {String} exportType
   * @returns {Object}
   * @description Get tracking content object for AA
   */
  getTrackingByExportType = (exportType) => get(this.currentRequestsByType, `${exportType}.tracking`);

  /* --------- HELPERS ---------*/

  /**
   * @ngdoc function
   * @name updateCurrentRequestByType
   * @param {String} exportType
   * @param {Object} [payload] Options selected by the user OR jobId of previous export
   * @param {Object} [tracking] Tracking object for AA
   * @description Update global currentRequestsByType by exportType
   */
  updateCurrentRequestByType = (exportType, payload, tracking) => {
    this.currentRequestsByType[exportType] = {
      payload: extend({}, payload || get(this.currentRequestsByType, `${exportType}.payload`), {
        requestId: Date.now().toString(),
      }),
      wsErrorOneTimeConsumer: null,
      // AA tracking
      tracking,
    };
  };

  /**
   * @ngdoc function
   * @name _wsDeferHandler
   * @param {String} exportType
   * @param {Object} exportSubject
   * @param {Object} response
   * @description Handler for Export deferred
   */
  _wsDeferHandler = (exportType, exportSubject, response) => {
    // rest restore error consumer if we are getting results
    if (this.currentRequestsByType[exportType]) {
      this.currentRequestsByType[exportType].wsErrorOneTimeConsumer = null;
    }

    // Append also the exportRequest in order to allow the directive to have
    // a deferred.cancel method.
    if (isObject(exportSubject) && isObject(response)) {
      response.cancel = exportSubject.cancel;
    }
    RXService.next(EXPORT_TAB_EVENTS[exportType].progressInfo, response);
  };

  /**
   * @ngdoc function
   * @name _wsErrorsHandler
   * @param {String} exportType
   * @param {Object} err
   * @description Handler for WS Export errors.
   */
  _wsErrorsHandler = (exportType, err) => {
    // Trick for consuming restore error
    if (isFunction(this.currentRequestsByType[exportType].wsErrorOneTimeConsumer)) {
      this.currentRequestsByType[exportType].wsErrorOneTimeConsumer(err);
      this.currentRequestsByType[exportType].wsErrorOneTimeConsumer = null;
    } else {
      NotifyDialogsService.openModalWindowPromise({
        title: EXPORT_TEXTS.errorDialogTitle,
        text: get(err, 'data.message'),
        type: 'error',
        confirmText: EXPORT_TEXTS.errorDialogConfirmText,
      })
        .then(() => {
          RXService.next(EXPORT_TAB_EVENTS[exportType].cancel);
          RXService.unsubscribe(EXPORT_EVENTS.error);
        })
        .catch(Logger.error);
    }
  };
}

const ExportRequestManager = new ExportRequestManagerService();
export default ExportRequestManager;
